import React, { Component, useState } from "react";
import MaskedInput from "react-text-mask";
import NumberFormat from "react-number-format";
import ReactTooltip from "react-tooltip";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import Show from "./show.component";
import { Field } from "redux-form";
import { required } from "./formValidators";
import { travelExemptionToolTip } from "./toolTips";

// Render function for each field in the form
export class renderField extends Component {
  setFocusToInput() {
    this.input.focus();
  }

  render() {
    const {
      input,
      label,
      type,
      id,
      notice,
      tooltip,
      placeholder,
      hasOtherIcon,
      onKeyUp,
      maxLength,
      showHideOnClick,
      showHideIcon,
      inputMode,
      meta: { touched, error, warning },
      e2eId,
    } = this.props;

    let hasError = touched && error; // Component has an error
    let tooltipClassName = "";
    if (hasError) {
      tooltipClassName += "has-error";
    }
    if (hasOtherIcon) {
      tooltipClassName += " has-other-icon";
    }
    return (
      <div>
        <label>{label}</label>
        <div className={tooltipClassName}>
          <div className={tooltip ? "field has-tooltip" : "field"}>
            {/* Tooltip for CVC */}
            <ReactTooltip effect="solid" type="light" />
            {tooltip && (
              <i className="material-icons tip" data-tip={tooltip} data-html={true}>
                help
              </i>
            )}
            <div className="showHide-container">
              <input
                {...input}
                id={id}
                placeholder={placeholder}
                type={type}
                onKeyUp={onKeyUp}
                maxLength={maxLength}
                inputMode={inputMode}
                ref={(node) => (this.input = node)}
                data-e2e-id={e2eId}
              />
              {showHideIcon !== undefined ? (
                <i onClick={() => showHideOnClick()} className="material-icons clickable">
                  {showHideIcon ? "visibility_off" : "visibility"}
                </i>
              ) : null}
            </div>
          </div>
          {notice && <span className="notice-message">{notice}</span>}
          {hasError && <span className="error-message">{error}</span>}
        </div>
      </div>
    );
  }
}

export const renderSelectField = ({
  input,
  label,
  type,
  options,
  states,
  disabled,
  meta: { touched, error, warning },
  e2eId,
  showSelectOption = true,
  labelColElement = null,
}) => {
  let hasError = touched && error; // Component has an error
  let listOfOptions = { ...options };

  const labelContainerContents = labelColElement ? (
    <div className="container-fluid">
      <div className="row p-0">
        <div className="col-9 p-0 m-0">
          <label>{label}</label>
        </div>
        <div className="col-3 p-0 m-0">{labelColElement}</div>
      </div>
    </div>
  ) : (
    <label>{label}</label>
  );

  return (
    <div>
      {labelContainerContents}
      <div className={hasError ? "has-error no-exclamation" : "no-exclamation"}>
        <div className="field">
          <select {...input} disabled={disabled} data-e2e-id={e2eId}>
            {showSelectOption && <option value="">Select</option>}
            {Object.values(listOfOptions).map((option) => {
              return (
                <option
                  key={option}
                  value={states ? option.split("-")[0].trim() : option}
                  data-e2e-id={option}
                >
                  {option}
                </option>
              );
            })}
          </select>
        </div>
        {hasError && <span className="error-message">{error}</span>}
      </div>
    </div>
  );
};

export const renderMaskedField = ({
  input,
  mask,
  label,
  type,
  inputMode,
  placeholder,
  meta: { touched, error, warning },
  e2eId,
}) => {
  let hasError = touched && error; // Component has an error
  return (
    <div>
      <label>{label}</label>
      <div className={hasError ? "has-error" : ""}>
        <div className="field">
          <MaskedInput
            {...input}
            type={type}
            placeholder={placeholder}
            mask={mask}
            guide={false}
            inputMode={inputMode}
            data-e2e-id={e2eId}
          />
        </div>
        {hasError && <span className="error-message">{error}</span>}
      </div>
    </div>
  );
};

const normalizeValue = (value) => {
  if (typeof value === "undefined" || value === null) {
    return 0;
  }
  return value;
};

export const normalizeOLBMoneyField = (value) => {
  // Step 1: Clean the input by removing non-numeric characters except for the decimal point.
  let cleanedValue = `${value}`.replace(/[^\d.]/g, "");

  // Handling the case where the user might enter multiple decimal points
  const decimalPoints = cleanedValue.match(/\./g) || [];
  while (decimalPoints.length > 1) {
    cleanedValue = cleanedValue.replace(".", ""); // Remove all decimal points
    decimalPoints.pop(); // Update the count
  }

  // Step 2: Ensure intuitive typing by moving the decimal point.
  // Remove the existing decimal point for calculation
  cleanedValue = cleanedValue.replace(".", "");

  if (cleanedValue.length === 1) {
    cleanedValue = "0.0" + cleanedValue;
  } else if (cleanedValue.length === 2) {
    cleanedValue = "0." + cleanedValue;
  } else {
    cleanedValue = cleanedValue.slice(0, -2) + "." + cleanedValue.slice(-2);
  }

  // Convert to number to remove any leading zeros
  const numericValue = Number(cleanedValue);

  // Step 3: Format the value as currency.
  const usdFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formattedValue = usdFormatter.format(numericValue);

  // Return the formatted value
  return formattedValue;
};

// Special component for formatting currency, adding commas etc
export const renderOLBStyleMoneyField = ({
  input,
  label,
  type,
  notice,
  changeFunction,
  disabled,
  placeholder,
  inputMode,
  meta: { touched, error, warning },
  e2eId,
}) => {
  let hasError = touched && error; // Component has an error
  return (
    <div>
      <label>{label}</label>
      <div className={hasError ? "has-error" : ""}>
        <div className="field">
          <input
            {...input}
            type={type}
            inputMode={inputMode}
            onChange={(event) => {
              const value = normalizeValue(event.target.value);

              const effectiveValue = normalizeOLBMoneyField(value);

              changeFunction(input.name, effectiveValue); // Add toFixed here as well to ensure only 2 decimal points are saved
            }}
            allowNegative={false}
            disabled={disabled}
            data-e2e-id={e2eId}
          />
        </div>
        {notice && <span className="notice-message">{notice}</span>}
        {hasError && <span className="error-message">{error}</span>}
      </div>
    </div>
  );
};

// Special component for formatting currency, adding commas etc
export const renderMoneyField = ({
  input,
  label,
  type,
  notice,
  changeFunction,
  disabled,
  placeholder,
  inputMode,
  meta: { touched, error, warning },
  e2eId,
}) => {
  let hasError = touched && error; // Component has an error
  return (
    <div>
      <label>{label}</label>
      <div className={hasError ? "has-error" : ""}>
        <div className="field">
          <NumberFormat
            {...input}
            type={type}
            decimalScale={2}
            fixedDecimalScale={true}
            thousandSeparator={true}
            prefix={"$"}
            inputMode={inputMode}
            onValueChange={(values) => {
              changeFunction(input.name, normalizeValue(values.floatValue).toFixed(2)); // Add toFixed here as well to ensure only 2 decimal points are saved
            }}
            allowNegative={false}
            disabled={disabled}
            data-e2e-id={e2eId}
          />
        </div>
        {notice && <span className="notice-message">{notice}</span>}
        {hasError && <span className="error-message">{error}</span>}
      </div>
    </div>
  );
};

// Render function for each field in the formd
export const renderToggleField = ({
  input,
  label,
  type,
  id,
  notice,
  placeholder,
  showModal,
  meta: { touched, error, warning },
  e2eId,
}) => {
  let hasError = touched && error; // Component has an error
  return (
    <div>
      <Toggle {...input} id={id} icons={false} />
      <label
        htmlFor={id}
        dangerouslySetInnerHTML={{ __html: label }}
        style={{ verticalAlign: "super", marginLeft: "10px", display: "inline-block" }}
      />
      <a
        href="#"
        style={{ fontSize: "16px", marginLeft: "8px", verticalAlign: "super", color: "#00558C" }}
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo(0, 0);
          showModal();
        }}
        data-e2e-id={e2eId}
      >
        What's this?
      </a>
      {notice && <span className="notice-message">{notice}</span>}
      {hasError && <span className="error-message">{error}</span>}
    </div>
  );
};

// Render function for each field in the formd
export const renderCheckboxField = ({
  input,
  label,
  type,
  id,
  notice,
  placeholder,
  meta: { touched, error, warning },
  e2eId,
}) => {
  let hasError = touched && error; // Component has an error
  return (
    <div>
      <div className="checkbox-container">
        <div className="checkbox">
          <input {...input} id={id} type={type} style={{ padding: "0" }} tabIndex={0} />
          <label
            className="m-0"
            htmlFor={id}
            dangerouslySetInnerHTML={{ __html: label }}
            data-e2e-id={e2eId}
          />
        </div>
        {notice && <span className="notice-message">{notice}</span>}
        {hasError && <span className="error-message">{error}</span>}
      </div>
    </div>
  );
};

// Render function for each field in the form
export const renderRadioField = ({
  input,
  label,
  type,
  id,
  className,
  notice,
  placeholder,
  containerClassNames,
  meta: { touched, error, warning },
  omitErrors = false,
  e2eId,
}) => {
  let hasError = touched && error; // Component has an error
  return (
    <div class={`radio-choice ${containerClassNames ? containerClassNames : ""}`}>
      <input {...input} id={id} className={className} placeholder={placeholder} type={type} />
      <label for={id} class="radio-label" data-e2e-id={e2eId}>
        {label}
      </label>

      <Show condition={!omitErrors}>
        {notice && <span className="notice-message">{notice}</span>}
        {hasError && <span className="error-message radio-error">{error}</span>}
      </Show>
    </div>
  );
};

export class renderTextarea extends Component {
  setFocusToInput() {
    this.input.focus();
  }

  render() {
    const {
      input,
      label,
      type,
      id,
      notice,
      placeholder,
      onKeyUp,
      maxLength,
      meta: { error, touched },
      e2eId,
    } = this.props;
    let hasError = touched && error; // Component has an error
    return (
      <div>
        <label>{label}</label>
        <div>
          <div className="field">
            <textarea
              {...input}
              id={id}
              placeholder={placeholder}
              type={type}
              onKeyUp={onKeyUp}
              maxLength={maxLength}
              ref={(node) => (this.input = node)}
              data-e2e-id={e2eId}
            ></textarea>
          </div>
          {notice && <span className="notice-message">{notice}</span>}
          {hasError && <span className="error-message">{error}</span>}
        </div>
      </div>
    );
  }
}

export const renderFieldArray =
  (options, descriptor, maxCount = null, maxText = null) =>
  ({ fields }) => {
    const [selectedOptions, setSelectedOptions] = useState([]);
    const addText = descriptor ? `+ Add Another ${descriptor}` : "+ Add";
    const maxString = maxCount ? (maxText ? maxText : "Maximum amount of values selected.") : null;

    const handleSelectChange = (index, option) => {
      const newSelectedOptions = [...selectedOptions];
      newSelectedOptions[index] = option;
      setSelectedOptions(newSelectedOptions);
    };

    let maxCountSelected = maxCount && fields.length >= maxCount;

    const handleRemove = (index) => {
      const newSelectedOptions = [...selectedOptions];
      newSelectedOptions.splice(index, 1);
      setSelectedOptions(newSelectedOptions);
      fields.remove(index);
    };

    return (
      <div>
        {fields.map((field, index) => {
          const removeText = `Remove`;
          const labelText = descriptor ? `Select ${descriptor}` : `Select ${index + 1}`;

          const labelElement = (
            <div className="d-flex">
              <span>{labelText}</span>
              <Show condition={index === 0}>
                <ReactTooltip effect="solid" type="light" />
                <i
                  className="material-icons toolTip pl-3"
                  data-tip={travelExemptionToolTip}
                  data-html={true}
                >
                  help
                </i>
              </Show>
            </div>
          );

          const availableOptions = options.filter((option) => !selectedOptions.includes(option));

          // Only include the selected option if it's an actual value
          // This prevents duplication of the generic "Select" option
          // And also ensure that the selected option for each dropdown is
          // preserved while omtting it for other dropdowns
          const availableOptionsAndSelected = selectedOptions[index]
            ? [...availableOptions, selectedOptions[index]]
            : availableOptions;

          const labelColElement = (
            <div>
              <Show condition={index > 0}>
                <button
                  className="link-style-button m-0"
                  type="button"
                  onClick={() => handleRemove(index)}
                >
                  {removeText}
                </button>
              </Show>
            </div>
          );

          return (
            <div className="mt-2" key={index}>
              <Field
                key={index}
                name={field}
                type="select"
                options={availableOptionsAndSelected}
                component={renderSelectField}
                label={labelElement}
                validate={[required]}
                onChange={(event) => handleSelectChange(index, event.target.value)}
                labelColElement={labelColElement}
              />
            </div>
          );
        })}
        <button
          className="link-style-button mb-3"
          type="button"
          onClick={() => fields.push()}
          disabled={maxCountSelected === true}
        >
          {addText}
        </button>
        <Show condition={maxCountSelected === true}>
          <p className="payment-message p-3">{maxString}</p>
        </Show>
      </div>
    );
  };



// WEBPACK FOOTER //
// ./src/utils/renderField.js